import { useState, useRef, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Page, Header } from 'components';
import { Card, CardContent } from '@mui/material';
import { makeStyles } from '@mui/styles';
import {
  amGetTables,
  amPollTables,
  amUpdateRevised,
} from '../actions/availabilityThunks';
import './AvailabilityManagement.css';

const useStyles = makeStyles((theme) => ({
  root:      { padding: theme.spacing(3) },
  container: { marginTop: theme.spacing(3) },
}));

// ─── HELPERS ──────────────────────────────────────────────────────────────────
const PAGE_SIZE = 50;
const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];
const POLL_INTERVAL = 30000;
const DECIMALS = 2;

// Backend sometimes sends dates as ambiguous "D/M/YYYY" slash strings instead
// of ISO. new Date('12/08/2026') assumes US MM/DD/YYYY, silently swapping day
// and month. Parse slash-separated dates explicitly as DD/MM/YYYY instead.
const parseFlexibleDate = (value) => {
  if (value instanceof Date) return value;
  if (typeof value === 'string') {
    const match = value.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})(?:[ T](\d{1,2}):(\d{2})(?::(\d{2}))?)?/);
    if (match) {
      const [, d, m, y, hh = '0', mm = '0', ss = '0'] = match;
      return new Date(Number(y), Number(m) - 1, Number(d), Number(hh), Number(mm), Number(ss));
    }
  }
  return new Date(value);
};

const formatDate = (value) => {
  if (!value || value === '0001-01-01T00:00:00' || value === '0001-01-01') return '';
  try {
    const d = parseFlexibleDate(value);
    if (isNaN(d.getTime())) return '';
    return d.toLocaleDateString('pt-PT', { day: '2-digit', month: '2-digit', year: 'numeric' });
  } catch { return ''; }
};

const formatDateTimeShort = (value) => {
  if (!value || value === '0001-01-01T00:00:00' || value === '0001-01-01') return '';
  try {
    const d = parseFlexibleDate(value);
    if (isNaN(d.getTime())) return '';
    return d.toLocaleString('pt-PT', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit', hour12: false,
    });
  } catch { return ''; }
};

// Month arrives as free text ("January/2026") — leave it as-is, only reformat
// when the backend sends a real date instead.
const formatMonth = (value) => {
  if (value == null || value === '') return '';
  const raw = String(value);
  if (/^[A-Za-z]/.test(raw)) return raw;
  return formatDate(raw) || raw;
};

// Availability / Blended are float on the BE, so a value stored as 99,47 can
// arrive as 99.47000122070312. Always round for display.
const toNumber = (value) => {
  if (value == null || value === '') return NaN;
  const n = Number(String(value).replace(',', '.'));
  return isNaN(n) ? NaN : n;
};

const formatNumber = (value) => {
  const n = toNumber(value);
  if (isNaN(n)) return value == null ? '' : String(value);
  return n.toLocaleString('pt-PT', { minimumFractionDigits: DECIMALS, maximumFractionDigits: DECIMALS });
};

// Value typed by the user → invariant string for the API ("99,5" → "99.5").
const toApiNumber = (value) => {
  const raw = String(value ?? '').trim().replace(',', '.');
  return raw === '' ? '' : raw;
};

const isBlank = (value) =>
  value == null || value === '' || String(value).toUpperCase() === 'NULL';

// The revised columns are non-nullable float on the BE, so an unset value
// arrives as 0 rather than null — show those as empty, not as "0,00".
const isBlankRevised = (value) => isBlank(value) || toNumber(value) === 0;

// ─── MONTH BADGE ──────────────────────────────────────────────────────────────
// One colour per distinct Month, stable across both tables. Values that name a
// calendar month follow calendar order; anything else falls back to a hash so
// the same text always gets the same colour.
const MONTH_PALETTE = [
  { color: '#2563eb', background: '#eff6ff' }, // jan
  { color: '#0891b2', background: '#ecfeff' }, // feb
  { color: '#0d9488', background: '#f0fdfa' }, // mar
  { color: '#16a34a', background: '#f0fdf4' }, // apr
  { color: '#65a30d', background: '#f7fee7' }, // may
  { color: '#ca8a04', background: '#fefce8' }, // jun
  { color: '#d97706', background: '#fffbeb' }, // jul
  { color: '#ea580c', background: '#fff7ed' }, // aug
  { color: '#dc2626', background: '#fef2f2' }, // sep
  { color: '#db2777', background: '#fdf2f8' }, // oct
  { color: '#c026d3', background: '#fdf4ff' }, // nov
  { color: '#7c3aed', background: '#f5f3ff' }, // dec
];

const MONTH_NAMES = [
  'january', 'february', 'march', 'april', 'may', 'june',
  'july', 'august', 'september', 'october', 'november', 'december',
];

const hashString = (text) => {
  let hash = 0;
  for (let i = 0; i < text.length; i += 1) hash = (hash * 31 + text.charCodeAt(i)) % 100000;
  return hash;
};

const monthPalette = (text) => {
  const key = String(text).trim().toLowerCase();
  const named = MONTH_NAMES.findIndex((m) => key.startsWith(m.slice(0, 3)));
  const index = named >= 0 ? named : hashString(key) % MONTH_PALETTE.length;
  return MONTH_PALETTE[index];
};

const MonthBadge = ({ value }) => {
  if (isBlank(value)) return null;
  const text = formatMonth(value);
  const { color, background } = monthPalette(text);
  return <span className="am-month-badge" style={{ color, backgroundColor: background }}>{text}</span>;
};

// ─── TABLES ───────────────────────────────────────────────────────────────────
// Each table has its own columns. `editable` marks the single inline-editable
// column, `numeric` gets decimal formatting, `month` renders the coloured
// badge, `datetime` gets dd/mm/yyyy hh:mm formatting.
const TABLES = [
  {
    key: 'availabilityTable',
    title: 'Availability',
    table: 'Availability',
    columns: [
      { key: 'month',               label: 'Month', month: true, badge: true },
      { key: 'polygon',             label: 'Polygon' },
      { key: 'type',                label: 'Type'    },
      { key: 'availability',        label: 'Availability', numeric: true },
      { key: 'availabilityRevised', label: 'Revised', numeric: true, editable: true },
      { key: 'changedBy',           label: 'Changed By' },
      { key: 'changedDate',         label: 'Changed Date', datetime: true },
    ],
  },
  {
    key: 'blendedTable',
    title: 'Blended',
    table: 'Blended',
    columns: [
      { key: 'month',          label: 'Month', month: true, badge: true },
      { key: 'polygon',        label: 'Polygon' },
      { key: 'type',           label: 'Type'    },
      { key: 'blended',        label: 'Blended', numeric: true },
      { key: 'blendedRevised', label: 'Revised', numeric: true, editable: true },
      { key: 'changedBy',      label: 'Changed By' },
      { key: 'changedDate',    label: 'Changed Date', datetime: true },
    ],
  },
];

// ─── ICON: SORT ───────────────────────────────────────────────────────────────
const SortIcon = ({ col, sortCol, sortDir }) => {
  if (sortCol !== col) return (
    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#cbd5e1" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginLeft: 4, flexShrink: 0 }}>
      <polyline points="6 9 12 3 18 9"/><polyline points="6 15 12 21 18 15"/>
    </svg>
  );
  return sortDir === 'asc'
    ? <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#1d4ed8" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginLeft: 4, flexShrink: 0 }}><polyline points="6 9 12 3 18 9"/></svg>
    : <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#1d4ed8" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginLeft: 4, flexShrink: 0 }}><polyline points="6 15 12 21 18 15"/></svg>;
};

// ─── EDITABLE CELL ────────────────────────────────────────────────────────────
// Click to edit, Enter/blur to save, Escape to cancel. Accepts both decimal
// separators; a non-numeric draft is marked red and discarded on commit.
const EditableCell = ({ value, disabled, onSave }) => {
  const [editing, setEditing] = useState(false);
  const [draft,   setDraft]   = useState('');
  const inputRef              = useRef(null);
  const committedRef          = useRef(false);

  useEffect(() => {
    if (editing && inputRef.current) inputRef.current.select();
  }, [editing]);

  const blank = isBlankRevised(value);
  const draftRaw = draft.trim();
  const invalid = draftRaw !== '' && isNaN(toNumber(draftRaw));

  const startEdit = () => {
    if (disabled) return;
    setDraft(blank ? '' : String(toNumber(value).toFixed(DECIMALS)).replace('.', ','));
    committedRef.current = false;
    setEditing(true);
  };

  const commit = () => {
    if (committedRef.current) return;
    committedRef.current = true;
    setEditing(false);
    if (invalid) return;

    const next = toApiNumber(draftRaw);
    const prev = blank ? '' : String(toNumber(value));
    if (next === prev) return;
    if (next !== '' && prev !== '' && Number(next) === Number(prev)) return;
    onSave(next);
  };

  const cancel = () => {
    committedRef.current = true;
    setEditing(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') { e.preventDefault(); commit(); }
    if (e.key === 'Escape') { e.preventDefault(); cancel(); }
  };

  if (editing) {
    return (
      <td className="am-td-editing">
        <input
          ref={inputRef}
          className={`am-cell-input${invalid ? ' am-cell-input--invalid' : ''}`}
          type="text"
          inputMode="decimal"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={commit}
          autoFocus
        />
      </td>
    );
  }

  return (
    <td
      className={`am-td-editable${disabled ? ' am-td-editable--disabled' : ''}`}
      onClick={startEdit}
      title={disabled ? undefined : 'Click to edit'}
    >
      {blank
        ? <span className="am-cell-placeholder">—</span>
        : formatNumber(value)}
    </td>
  );
};

// ─── TABLE ────────────────────────────────────────────────────────────────────
function AvailabilityTable({ title, columns, rows, loading, saving, onSaveRevised }) {
  const [page,     setPage]     = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const [search,   setSearch]   = useState('');
  const [sortCol,  setSortCol]  = useState(null);
  const [sortDir,  setSortDir]  = useState('asc');

  useEffect(() => { setPage(1); }, [rows.length, search, pageSize]);

  const handlePageSizeChange = (value) => {
    setPageSize(Number(value));
    setPage(1);
  };

  const handleSort = (key) => {
    if (sortCol === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortCol(key);
      setSortDir('asc');
    }
    setPage(1);
  };

  const cellText = (row, col) => {
    const value = row[col.key];
    if (col.month) return formatMonth(value);
    if (col.datetime) return formatDateTimeShort(value);
    if (col.numeric) return isBlank(value) ? '' : formatNumber(value);
    return value == null ? '' : String(value);
  };

  const searchMatch = (row, q) => {
    if (!q) return true;
    const s = q.toLowerCase();
    return columns.some((col) => cellText(row, col).toLowerCase().includes(s));
  };

  const compare = (a, b, col) => {
    const key = col?.key ?? sortCol;
    if (col?.numeric) {
      const na = toNumber(a[key]);
      const nb = toNumber(b[key]);
      if (!isNaN(na) && !isNaN(nb)) return na - nb;
    }
    return String(a[key] ?? '').localeCompare(String(b[key] ?? ''), undefined, { numeric: true, sensitivity: 'base' });
  };

  const filtered = rows.filter((r) => searchMatch(r, search));

  const sortColumn = columns.find((c) => c.key === sortCol);
  const sorted = sortCol
    ? [...filtered].sort((a, b) => {
        const cmp = compare(a, b, sortColumn);
        return sortDir === 'asc' ? cmp : -cmp;
      })
    : filtered;

  const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize));
  const paged      = sorted.slice((page - 1) * pageSize, page * pageSize);

  return (
    <div className="am-card">
      <div className="am-card-header">
        <span className="am-card-title">{title}</span>
        <span className="am-card-count">{filtered.length}</span>
      </div>

      <div className="am-search-bar">
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
        </svg>
        <input
          className="am-search-input"
          type="text"
          placeholder="Search…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        {search && (
          <button className="am-search-clear" onClick={() => setSearch('')}>✕</button>
        )}
      </div>

      <div className="am-table-wrapper">
        <table className="am-table">
          <thead>
            <tr>
              {columns.map((col) => (
                <th
                  key={col.key}
                  className={`am-th-sortable${col.numeric ? ' am-th-numeric' : ''}`}
                  onClick={() => handleSort(col.key)}
                >
                  {col.label}
                  <SortIcon col={col.key} sortCol={sortCol} sortDir={sortDir} />
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading && rows.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className="am-table-empty">
                  <span className="am-spinner" style={{ marginRight: 8 }} />
                  Loading...
                </td>
              </tr>
            ) : sorted.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className="am-table-empty">No data available</td>
              </tr>
            ) : paged.map((row, idx) => (
              <tr key={row.id ?? idx}>
                {columns.map((col) => {
                  if (col.editable) {
                    return (
                      <EditableCell
                        key={col.key}
                        value={row[col.key]}
                        disabled={saving}
                        onSave={(next) => onSaveRevised(row, next)}
                      />
                    );
                  }
                  const text = cellText(row, col);
                  return (
                    <td key={col.key} className={col.numeric ? 'am-td-numeric' : undefined} title={text || undefined}>
                      {col.badge ? <MonthBadge value={row[col.key]} /> : text}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="am-pagination">
        <div className="am-page-size">
          <span>Rows:</span>
          <select
            className="am-page-size-select"
            value={pageSize}
            onChange={(e) => handlePageSizeChange(e.target.value)}
          >
            {PAGE_SIZE_OPTIONS.map((n) => (
              <option key={n} value={n}>{n}</option>
            ))}
          </select>
        </div>
        {sorted.length > pageSize && (
          <div className="am-page-nav">
            <button className="am-page-btn" onClick={() => setPage(1)}            disabled={page === 1}>«</button>
            <button className="am-page-btn" onClick={() => setPage((p) => p - 1)} disabled={page === 1}>‹</button>
            <span className="am-page-info">{page} / {totalPages}</span>
            <button className="am-page-btn" onClick={() => setPage((p) => p + 1)} disabled={page === totalPages}>›</button>
            <button className="am-page-btn" onClick={() => setPage(totalPages)}   disabled={page === totalPages}>»</button>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── COMPONENT ────────────────────────────────────────────────────────────────
function AvailabilityManagement() {
  const classes  = useStyles();
  const dispatch = useDispatch();

  const loading           = useSelector((state) => state.availabilityManagement.loading);
  const availabilityTable = useSelector((state) => state.availabilityManagement.availabilityTable);
  const blendedTable      = useSelector((state) => state.availabilityManagement.blendedTable);
  const userId            = useSelector((state) => state.session.user?.id);

  const pollingRef = useRef(null);

  useEffect(() => {
    dispatch(amGetTables());
    pollingRef.current = setInterval(() => dispatch(amPollTables()), POLL_INTERVAL);
    return () => {
      if (pollingRef.current) clearInterval(pollingRef.current);
      pollingRef.current = null;
    };
  }, []);

  const dataByKey = {
    availabilityTable,
    blendedTable,
  };

  // Matches AvailabilityRevisedUpdateDto. `table` tells the BE which table and
  // therefore which column to write; the new value always travels in
  // valueRevised, as an invariant string ("99.5"), empty to clear.
  const handleSaveRevised = (table) => (row, value) => {
    dispatch(amUpdateRevised(userId, {
      id:           row.id,
      table,
      dateWritten:  row.dateWritten,
      date:         row.date,
      polygon:      row.polygon,
      type:         row.type ?? null,
      valueRevised: value,
    }));
  };

  return (
    <Page className={classes.root} title="Availability Management">
      <Header module="NASP" view="Availability Management" />
      <Card className={classes.container}>
        <CardContent style={{ padding: 0 }}>
          <div className="am-root">
            <div className="am-grid">
              {TABLES.map((t) => (
                <AvailabilityTable
                  key={t.key}
                  title={t.title}
                  columns={t.columns}
                  rows={dataByKey[t.key] ?? []}
                  loading={loading.tables}
                  saving={loading.updateRevised}
                  onSaveRevised={handleSaveRevised(t.table)}
                />
              ))}
            </div>
          </div>
        </CardContent>
      </Card>
    </Page>
  );
}

export default AvailabilityManagement;
